import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CartContext } from '../contexts/CartContext';
import useEcontOffices from '../../hooks/useEcontOffices';
import useSaveOrder from "../../hooks/useSaveOrder";
import styles from './OrderForm.module.css';

const OrderForm = () => {
    const navigate = useNavigate();
    const [isOrdered, setIsOrdered] = useState(false);
    const { cartItems, removeFromCart, clearCart } = useContext(CartContext);
    const [cityFilter, setCityFilter] = useState('');
    const [deliveryMethod, setDeliveryMethod] = useState('econt');
    const [totalPrice, setTotalPrice] = useState(0);
    const [discountedPrice, setDiscountedPrice] = useState(0);
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        note: '',
        officeCode: '',
        finalPrice: 0.00
    });

    const { submitOrder } = useSaveOrder();

    const { offices } = useEcontOffices();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (cartItems.length === 0) {
            setSubmitError('Количката е празна. Добави продукти преди да завършиш поръчката.');
            return;
        }

        setSubmitError('');
        setIsSubmitting(true);

        const officeCode = deliveryMethod === 'econt' ? formData.officeCode : '0';
        const finalPrice = discountedPrice;

        const isOrderSuccessful = await submitOrder({ ...formData, officeCode, finalPrice }, cartItems, cityFilter, clearCart);

        setIsSubmitting(false);

        if (!isOrderSuccessful) {
            setSubmitError('Възникна проблем при изпращането. Опитай отново.');
            return;
        }

        setIsOrdered(true);

        setTimeout(() => {
            setIsOrdered(false);
            navigate("/");
        }, 4000);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        const nextValue = name === 'phone'
            ? value.replace(/\D/g, '').slice(0, 10) // keep only digits, max 10
            : value;

        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: nextValue,
            ...(name === "office" && { address: value }),
            ...(name === "officeCode" && { officeCode: value })
        }));
    };

    const handleOfficeChange = (e) => {
        const selectedOffice = offices.find(office => office.code === e.target.value);
        if (selectedOffice) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                address: `Офис: ${selectedOffice.name} ; Адрес: ${selectedOffice.address.fullAddress}`,
                officeCode: selectedOffice.code
            }));
        }
    };

    const handleCityFilterChange = (e) => {
        setCityFilter(e.target.value);
    };

    const handleDeliveryMethodChange = (e) => {
        setDeliveryMethod(e.target.value);
    };

    const filteredOffices = offices.filter((office) => {
        const fullAddress = office.address?.fullAddress?.toLowerCase().trim() || "";
        const searchInput = cityFilter.toLowerCase().trim();

        return fullAddress.includes(searchInput);
    });

    useEffect(() => {
        const total = cartItems.reduce((sum, item) => {
            return sum + (parseFloat(item.discount_price.replace(/[^\d.-]/g, '')) * item.quantity);
        }, 0);

        let discount = 0;
        const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

        if (totalItems === 2) {
            discount = 0.10;
        } else if (totalItems >= 3) {
            discount = 0.15;
        }

        const discountedTotal = total - (total * discount);

        setTotalPrice(total.toFixed(2));
        setDiscountedPrice(discountedTotal.toFixed(2));
        setDiscountPercentage((discount * 100).toFixed(0));
    }, [cartItems]);

    const deliveryCost = deliveryMethod === 'econt' ? 4 : 6;
    const grandTotal = (Number(discountedPrice) + deliveryCost).toFixed(2);
    const totalInBGN = (grandTotal * 1.9558).toFixed(2);

    return (
        <div className={styles.orderPage}>
            <div className={styles.pageHeader}>
                <div className={styles.eyebrow}>Завърши поръчката</div>
                <h1>Доставка и детайли</h1>
                <p>Попълни данните за получаване и прегледай резюмето на количката си.</p>
            </div>

            <div className={styles.grid}>
                <form className={styles.formCard} onSubmit={handleSubmit}>
                    <h2>Данни за получател</h2>
                    <div className={styles.fieldGrid}>
                        <div className={styles.formGroup}>
                            <label>Име</label>
                            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required placeholder="Иван" />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Фамилия</label>
                            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required placeholder="Иванов" />
                        </div>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Телефон</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="08XXXXXXXX"
                            pattern="08[0-9]{8}"
                            minLength={10}
                            maxLength={10}
                            inputMode="numeric"
                            title="Въведи телефон във формат 08XXXXXXXX (10 цифри)"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Доставка до</label>
                        <div className={styles.deliveryMethod}>
                            <label>
                                <input
                                    type="radio"
                                    name="deliveryMethod"
                                    value="econt"
                                    checked={deliveryMethod === 'econt'}
                                    onChange={handleDeliveryMethodChange}
                                />
                                Офис на куриер
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="deliveryMethod"
                                    value="address"
                                    checked={deliveryMethod === 'address'}
                                    onChange={handleDeliveryMethodChange}
                                />
                                До адрес
                            </label>
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>{deliveryMethod === 'econt' ? "Град/населено място" : "Град/населено място"}</label>
                        <input
                            type="text"
                            value={cityFilter}
                            onChange={handleCityFilterChange}
                            placeholder="Търси офис или адрес"
                            required
                        />
                    </div>

                    {deliveryMethod === 'econt' && (
                        <div className={styles.formGroup}>
                            <label>Избери офис</label>
                            <select
                                name="office"
                                value={formData.officeCode}
                                onChange={handleOfficeChange}
                                required
                            >
                                <option value="">Избери офис</option>
                                {filteredOffices.length > 0 ? (
                                    filteredOffices.map((office) => (
                                        <option key={office.code} value={office.code}>
                                            {office.name || "Офис"} - {office.address.settlement?.name || office.address.fullAddress || "Адрес"}
                                        </option>
                                    ))
                                ) : (
                                    <option value="">Няма намерени офиси</option>
                                )}
                            </select>
                        </div>
                    )}

                    {deliveryMethod === 'address' && (
                        <div className={styles.formGroup}>
                            <label>Адрес</label>
                            <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Улица, номер, вход, апартамент"
                                required
                            />
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label>Бележка към поръчката</label>
                        <textarea
                            name="note"
                            value={formData.note}
                            onChange={handleChange}
                            placeholder="Специални инструкции за доставка или продукт"
                            rows="4"
                        />
                    </div>

                    {submitError && <div className={styles.errorBox}>{submitError}</div>}

                    <button type="submit" className={styles.submitButton} disabled={isSubmitting || cartItems.length === 0}>
                        {isSubmitting ? 'Изпращане...' : 'Завърши поръчката'}
                    </button>
                    <Link className={styles.secondaryButton} to="/products">Продължи пазаруването</Link>
                </form>

                <aside className={styles.summaryCard}>
                    <div className={styles.summaryHeader}>
                        <p className={styles.cartEyebrow}>Резюме</p>
                        <h2>Твоята количка</h2>
                    </div>

                    {cartItems.length === 0 ? (
                        <div className={styles.emptyState}>
                            <i className="fas fa-shopping-bag"></i>
                            <p>Количката е празна.</p>
                            <Link className={styles.secondaryButton} to="/products">Към продуктите</Link>
                        </div>
                    ) : (
                        <>
                            <ul className={styles.cartItemsList}>
                                {cartItems.map(item => {
                                    const itemPrice = parseFloat(item.discount_price.replace(/[^\d.-]/g, '')) || 0;
                                    const lineTotal = (itemPrice * item.quantity).toFixed(2);
                                    const primaryImage =
                                        item.images?.find((image) => image.is_primary)?.image_url ||
                                        item.images?.[0]?.image_url ||
                                        "/images/placeholder.png";

                                    return (
                                        <li key={`${item.id}-${item.option || "default"}`} className={styles.cartItem}>
                                            <div className={styles.itemThumb}>
                                                <img src={primaryImage} alt={item.productname} />
                                            </div>
                                            <div className={styles.itemInfo}>
                                                <div className={styles.itemTopRow}>
                                                    <span className={styles.itemName}>{item.productname}</span>
                                                    <button className={styles.removeItemFromCart} type="button" onClick={() => removeFromCart(item.id)}>Премахни</button>
                                                </div>
                                                <div className={styles.itemMeta}>
                                                    <span className={styles.itemQuantity}>Количество: {item.quantity}</span>
                                                    <span className={styles.itemPrice}>{lineTotal} €</span>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className={styles.totalBox}>
                                <div className={styles.totalRow}>
                                    <span>Междинна сума</span>
                                    <span className={styles.cartOldPrice}>{totalPrice} €</span>
                                </div>
                                {totalPrice !== discountedPrice && (
                                    <div className={styles.totalRow}>
                                        <span>Отстъпка</span>
                                        <span className={styles.cartDiscountText}>-{discountPercentage}%</span>
                                    </div>
                                )}
                                <div className={styles.totalRow}>
                                    <span>След отстъпка</span>
                                    <span className={styles.cartNewPrice}>{discountedPrice} €</span>
                                </div>
                                <div className={styles.totalRow}>
                                    <span>Доставка ({deliveryMethod === 'econt' ? 'офис' : 'адрес'})</span>
                                    <span>{deliveryCost.toFixed(2)} €</span>
                                </div>
                                <div className={`${styles.totalRow} ${styles.grandTotalRow}`}>
                                    <span>Общо</span>
                                    <span>{grandTotal} €</span>
                                </div>
                                <div className={styles.secondaryTotal}>
                                    ≈ {totalInBGN} лв. (ориентировъчно)
                                </div>
                            </div>
                        </>
                    )}
                </aside>
            </div>

            {isOrdered && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <p>Успешно изпратихме поръчката! Благодарим ти.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderForm;
